/**
 * Create GitHub Copilot Sandbox page component.
 * Simplified sandbox creation with GitHub Copilot preset.
 *
 * @module pages/CreateCopilotSandboxPage
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router';
import {
  ArrowLeft,
  Save,
  Github,
  ChevronDown,
  Plug,
  ExternalLink,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Select,
  LabelsEditor,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { useCreateSandbox, useSandboxGroups, useSandboxGroup } from '@/hooks';
import { connectionService } from '@/services';
import type { SandboxRequest, ConnectionResponse, ConnectionTypeResponse } from '@/types';

/** Polling interval for checking authorization status (3 seconds) */
const AUTH_POLL_INTERVAL = 3000;

/** Get display name for a connection type */
const getDisplayName = (type: ConnectionTypeResponse): string =>
  type.displayName?.trim() || type.name;

interface FormState {
  labels: Record<string, string>;
  sandboxGroupId: string;
  selectedConnections: string[];
}

const initialFormState: FormState = {
  labels: {},
  sandboxGroupId: '',
  selectedConnections: [],
};

/**
 * Create GitHub Copilot sandbox page
 */
export function CreateCopilotSandboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMutation = useCreateSandbox();
  const { data: sandboxGroups, isLoading: sandboxGroupsLoading } = useSandboxGroups(1, 100);

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  // Get groupId from URL params if coming from a group page
  const preselectedGroupId = searchParams.get('groupId') || '';

  // Fetch preselected group details to inherit connectors
  const { data: preselectedGroup } = useSandboxGroup(preselectedGroupId || undefined);

  const [formState, setFormState] = useState<FormState>(() => ({
    ...initialFormState,
    sandboxGroupId: preselectedGroupId,
  }));

  const [error, setError] = useState<string | null>(null);

  // Connector state
  const [isConnectorsExpanded, setIsConnectorsExpanded] = useState(false);
  const [connectionTypes, setConnectionTypes] = useState<ConnectionTypeResponse[]>([]);
  const [existingConnections, setExistingConnections] = useState<ConnectionResponse[]>([]);
  const [isLoadingConnectors, setIsLoadingConnectors] = useState(true);
  const [authorizingConnector, setAuthorizingConnector] = useState<string | null>(null);
  const [pollingConnector, setPollingConnector] = useState<string | null>(null);

  // Load connector types and existing connections on mount
  useEffect(() => {
    const loadConnectorData = async () => {
      try {
        const [types, connections] = await Promise.all([
          connectionService.listTypes(),
          connectionService.list(),
        ]);
        // Only show stable connectors
        const stableTypes = types.filter((t) => t.stability === 'stable');
        setConnectionTypes(stableTypes);
        setExistingConnections(connections);

        // Auto-select github-copilot connector if it's authorized
        const githubCopilotConnection = connections.find(
          (c) => c.type.toLowerCase() === 'github-copilot' && c.state === 'Ready'
        );
        if (githubCopilotConnection) {
          setFormState((prev) => ({
            ...prev,
            selectedConnections: [...prev.selectedConnections, githubCopilotConnection.id],
          }));
        }
      } catch (err) {
        console.error('Failed to load connector data:', err);
      } finally {
        setIsLoadingConnectors(false);
      }
    };
    loadConnectorData();
  }, []);

  // Auto-expand connectors section if github-copilot is not authorized
  useEffect(() => {
    if (!isLoadingConnectors) {
      const githubCopilotConnection = existingConnections.find(
        (c) => c.type.toLowerCase() === 'github-copilot'
      );
      if (!githubCopilotConnection || githubCopilotConnection.state !== 'Ready') {
        setIsConnectorsExpanded(true);
      }
    }
  }, [isLoadingConnectors, existingConnections]);

  // Poll for authorization status when a connector is being authorized
  useEffect(() => {
    if (!pollingConnector) return;

    const pollInterval = setInterval(async () => {
      try {
        const refreshed = await connectionService.refresh(pollingConnector);
        setExistingConnections((prev) =>
          prev.map((c) => (c.id === pollingConnector ? refreshed : c))
        );

        if (refreshed.state === 'Ready') {
          // Authorization complete - stop polling and add to selected
          setPollingConnector(null);
          setFormState((prev) => {
            if (prev.selectedConnections.includes(refreshed.id)) {
              return prev;
            }
            return { ...prev, selectedConnections: [...prev.selectedConnections, refreshed.id] };
          });
        }
      } catch (err) {
        console.error('Failed to poll connector status:', err);
      }
    }, AUTH_POLL_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [pollingConnector]);

  // Keep GitHub Copilot selected once authorized (required connector)
  useEffect(() => {
    const requiredCopilotConnection = existingConnections.find(
      (c) => c.type.toLowerCase() === 'github-copilot' && c.state === 'Ready'
    );

    if (!requiredCopilotConnection) {
      return;
    }

    setFormState((prev) => {
      if (prev.selectedConnections.includes(requiredCopilotConnection.id)) {
        return prev;
      }

      return {
        ...prev,
        selectedConnections: [...prev.selectedConnections, requiredCopilotConnection.id],
      };
    });
  }, [existingConnections]);

  // Get existing connection for a connector type
  const getConnectionForType = useCallback(
    (typeName: string): ConnectionResponse | undefined => {
      return existingConnections.find((c) => c.type.toLowerCase() === typeName.toLowerCase());
    },
    [existingConnections]
  );

  // Handle authorize button click
  const handleAuthorize = async (type: ConnectionTypeResponse) => {
    setAuthorizingConnector(type.name);
    try {
      let connection = getConnectionForType(type.name);

      // Create connection if it doesn't exist
      if (!connection) {
        connection = await connectionService.create({
          type: type.name,
          name: getDisplayName(type),
        });
        setExistingConnections((prev) => [...prev, connection!]);
      }

      // If connection is already Ready (e.g., first-party auth succeeded), no OAuth needed
      if (connection.state === 'Ready') {
        return;
      }

      // Generate consent link and open in new tab
      const redirectUrl = window.location.href;
      const consentLink = await connectionService.generateConsentLink(connection.id, redirectUrl);
      window.open(consentLink, '_blank');

      // Start polling for authorization status
      setPollingConnector(connection.id);
    } catch (err) {
      console.error('Failed to authorize connector:', err);
      setError('Failed to start authorization. Please try again.');
    } finally {
      setAuthorizingConnector(null);
    }
  };

  // Handle checkbox toggle for an authorized connector
  const handleConnectorToggle = (connectionId: string, isChecked: boolean) => {
    const toggledConnection = existingConnections.find((c) => c.id === connectionId);
    const isRequiredCopilotConnection =
      toggledConnection?.type.toLowerCase() === 'github-copilot' &&
      toggledConnection.state === 'Ready';

    if (!isChecked && isRequiredCopilotConnection) {
      return;
    }

    setFormState((prev) => {
      if (isChecked) {
        if (prev.selectedConnections.includes(connectionId)) {
          return prev;
        }
        return { ...prev, selectedConnections: [...prev.selectedConnections, connectionId] };
      } else {
        return {
          ...prev,
          selectedConnections: prev.selectedConnections.filter((id) => id !== connectionId),
        };
      }
    });
  };

  const handleInputChange = (
    field: keyof FormState,
    value: string | Record<string, string> | string[]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Commit any uncommitted label draft
    labelsEditorRef.current?.commitDraft();
    const finalLabels = formState.labels;

    const requiredCopilotConnectionId = existingConnections.find(
      (c) => c.type.toLowerCase() === 'github-copilot' && c.state === 'Ready'
    )?.id;

    // Combine group connections with selected connections
    const allConnections = [
      ...(preselectedGroup?.connections || []),
      ...formState.selectedConnections,
      ...(requiredCopilotConnectionId ? [requiredCopilotConnectionId] : []),
    ];
    // Deduplicate
    const uniqueConnections = [...new Set(allConnections)];

    // Build the request with GitHubCopilot preset
    const request: SandboxRequest = {
      presetSandboxType: 'GitHubCopilot',
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
      sandboxGroupId: formState.sandboxGroupId || undefined,
      // Include selected connections
      connections: uniqueConnections.length > 0 ? uniqueConnections : undefined,
    };

    try {
      const sandbox = await createMutation.mutateAsync(request);

      // Navigate based on whether we came from a group
      if (formState.sandboxGroupId) {
        navigate(`/sandbox-groups/${encodeURIComponent(formState.sandboxGroupId)}`);
      } else {
        navigate(`/sandboxes/${encodeURIComponent(sandbox.id)}`);
      }
    } catch (err: unknown) {
      console.error('Failed to create sandbox:', err);
      // ApiException has a 'detail' property with the user-friendly message
      const apiErr = err as { detail?: string; message?: string };
      const errorMessage = apiErr.detail || apiErr.message || 'Unknown error';
      setError(errorMessage);
    }
  };

  const isSubmitting = createMutation.isPending;

  // Check if github-copilot connector is authorized (required for this page)
  const githubCopilotConnection = existingConnections.find(
    (c) => c.type.toLowerCase() === 'github-copilot'
  );
  const isGithubCopilotAuthorized = githubCopilotConnection?.state === 'Ready';
  const isPollingGithubCopilot = pollingConnector === githubCopilotConnection?.id;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Create GitHub Copilot Sandbox"
        description="Create a sandbox pre-configured for GitHub Copilot with ubuntu image and default resources"
        breadcrumbs={[
          { label: 'Sandboxes', href: '/sandboxes' },
          { label: 'Create Copilot Sandbox' },
        ]}
      />

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader title="Configuration" description="Configure your GitHub Copilot sandbox" />
          <CardBody className="space-y-6">
            {/* Preset Info */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Github className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-100">
                    GitHub Copilot Preset
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    This sandbox will be created with:
                  </p>
                  <ul className="text-sm text-green-700 dark:text-green-300 mt-2 list-disc list-inside space-y-1">
                    <li>Ubuntu public disk image</li>
                    <li>4 CPU cores, 8GB RAM, 10GB disk</li>
                    <li>Cloud Hypervisor VMM</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Sandbox Group */}
            <div>
              <Select
                label="Sandbox Group (Optional)"
                options={[
                  { value: '', label: 'No group' },
                  ...(sandboxGroups?.map((group) => {
                    const name =
                      group.labels && typeof group.labels === 'object'
                        ? (group.labels as Record<string, string>)['name'] || group.id
                        : group.id;
                    return { value: group.id, label: name };
                  }) || []),
                ]}
                value={formState.sandboxGroupId}
                onChange={(e) => handleInputChange('sandboxGroupId', e.target.value)}
                disabled={sandboxGroupsLoading || !!preselectedGroupId}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optionally associate this sandbox with a group. Connections from the group will be
                inherited.
              </p>
            </div>

            {/* Labels */}
            <div>
              <LabelsEditor
                ref={labelsEditorRef}
                value={formState.labels}
                onChange={(labels) => handleInputChange('labels', labels)}
                label="Labels (Optional)"
              />
            </div>

            {/* Authorization Required Warning */}
            {!isLoadingConnectors && !isGithubCopilotAuthorized && !isPollingGithubCopilot && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900 dark:text-amber-100">
                      Authorization Required
                    </h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      You must authorize the GitHub Copilot connector before creating this sandbox.
                      Expand the Connectors section below and click "Authorize".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Connectors (Collapsible) */}
            <div
              className={`border rounded-lg overflow-hidden ${!isGithubCopilotAuthorized ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}
            >
              <button
                type="button"
                onClick={() => setIsConnectorsExpanded(!isConnectorsExpanded)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Plug className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Connectors (Optional)
                  </span>
                  {formState.selectedConnections.length > 0 && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                      {formState.selectedConnections.length} selected
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    isConnectorsExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isConnectorsExpanded && (
                <div className="p-4 space-y-3">
                  {isLoadingConnectors ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">Loading connectors...</span>
                    </div>
                  ) : connectionTypes.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No connectors available
                    </p>
                  ) : (
                    connectionTypes.map((type) => {
                      const connection = getConnectionForType(type.name);
                      const isAuthorized = connection?.state === 'Ready';
                      const isPolling = pollingConnector === connection?.id;
                      const isAuthorizing = authorizingConnector === type.name;
                      const isRequiredCopilot = type.name.toLowerCase() === 'github-copilot';
                      const isSelected = connection
                        ? formState.selectedConnections.includes(connection.id)
                        : false;

                      return (
                        <div
                          key={type.name}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {type.iconUrl ? (
                              <img
                                src={type.iconUrl}
                                alt=""
                                className="h-5 w-5 object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Plug className="h-5 w-5 text-gray-400" />
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {getDisplayName(type)}
                            </span>
                          </div>

                          {isAuthorized ? (
                            <input
                              type="checkbox"
                              checked={isRequiredCopilot ? true : isSelected}
                              onChange={(e) =>
                                handleConnectorToggle(connection!.id, e.target.checked)
                              }
                              disabled={isRequiredCopilot}
                              className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${
                                isRequiredCopilot
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer'
                              }`}
                            />
                          ) : isPolling ? (
                            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Waiting for authorization...</span>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => handleAuthorize(type)}
                              disabled={isAuthorizing}
                              leftIcon={
                                isAuthorizing ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ExternalLink className="h-3 w-3" />
                                )
                              }
                            >
                              {isAuthorizing ? 'Opening...' : 'Authorize'}
                            </Button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </CardBody>
          <CardFooter className="flex justify-between">
            <Link
              to={
                preselectedGroupId
                  ? `/sandbox-groups/${encodeURIComponent(preselectedGroupId)}`
                  : '/sandboxes'
              }
            >
              <Button
                type="button"
                variant="secondary"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={isSubmitting || !isGithubCopilotAuthorized}
              leftIcon={<Save className="h-4 w-4" />}
            >
              {isSubmitting ? 'Creating...' : 'Create Sandbox'}
            </Button>
          </CardFooter>
        </Card>

        {error && (
          <Card className="border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
            <CardBody>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </CardBody>
          </Card>
        )}
      </form>
    </div>
  );
}
